import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ARROW_SCOPE_ATTR } from './RecordPager';

/**
 * ImageCarousel — one large image at a time with slide navigation.
 *
 * NOT the viewer for records that carry several frames of the same moment. The alert, event and
 * attendance-sighting records moved to SnapshotGallery: they show the wide shot plus the crops
 * taken out of it, which are read together rather than one after another, and this caps every
 * image at its natural size — which left a 64px face crop as a stamp in a black rectangle.
 * Reach for this one only where the pictures are unrelated and there are few of them.
 *
 * `snapshotTypeRank` / `snapshotTypeLabel` live here and are still used everywhere, gallery
 * included.
 *
 * Every place that shows what a camera captured uses this, so a snapshot reads the same
 * whether it came from an alert or an attendance sighting: full width of its container, one
 * frame at a time, with arrows, a thumbnail strip, arrow keys and touch swipe all moving the
 * same index. Broken images keep their slot (with a placeholder) so the indices stay aligned
 * with the captions.
 */
export interface CarouselImage {
  url: string;
  label?: string;
}

export interface ImageCarouselProps {
  images: CarouselImage[];
  className?: string;
  /** Given, the displayed frame becomes clickable — used to hand off to a lightbox. */
  onImageClick?: (image: CarouselImage, index: number) => void;
  /**
   * Who owns the left/right keys.
   *
   * `global` (the default) — the carousel is the only thing on screen that pages, so it takes
   * them from anywhere. `focused` — something OUTSIDE pages too (a RecordPager stepping through
   * a list of alerts or events), so the carousel only claims the keys once the reader has
   * clicked or tabbed into it. Until then left/right change the record; after, the snapshot.
   */
  arrowKeys?: 'global' | 'focused';
  /** Frame shape, replacing the default 16:9 box — e.g. `h-[46vh]` in a full-screen record. */
  frameClassName?: string;
  /**
   * A second chip beside the frame's own type label — the one fact that belongs ON the image
   * rather than in the record beside it, which in practice is the moment it was captured.
   */
  badge?: ReactNode;
  /**
   * Pinned to the bottom-right of the frame. For the measurement the snapshot is evidence FOR
   * (the alert's confidence), which reads as a caption on the image and as trivia in a grid.
   */
  overlay?: ReactNode;
}

/**
 * Snapshot display order: the wide shot first, then the crops taken out of it.
 *
 * The AI payload's key order puts crops before the full frame, so left as-is a carousel opens
 * on a face crop with no idea what it was cropped from. Anything unrecognised sorts last, in
 * payload order — a new image type appearing is not a reason to hide it.
 */
const SNAPSHOT_TYPE_ORDER = ['fullFrame', 'personCrop', 'faceCrop', 'alignedFace'];

export function snapshotTypeRank(type: string): number {
  const i = SNAPSHOT_TYPE_ORDER.indexOf(type);
  return i === -1 ? SNAPSHOT_TYPE_ORDER.length : i;
}

/**
 * "personCrop" → "person Crop", for a caption.
 *
 * The trailing-digit rule is what makes a crowded frame readable. One event now carries a crop
 * PER PERSON, numbered by the module (`personCrop0 … personCrop6`), and glued to the word the
 * strip read "person Crop0, person Crop1, …" — a column of near-identical labels in which the
 * digit, the only thing telling them apart, is the least visible character.
 */
export function snapshotTypeLabel(type: string): string {
  return type
    .replace(/[_-]+/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .trim();
}

export default function ImageCarousel({
  images = [],
  className = '',
  onImageClick,
  arrowKeys = 'global',
  frameClassName,
  badge,
  overlay,
}: ImageCarouselProps) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<number, boolean>>({});
  const touchStartX = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const count = images.length;
  const multiple = count > 1;

  // Clamp when the image list shrinks under us (e.g. alert reloaded)
  useEffect(() => {
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  const go = useCallback((delta: number) => {
    if (count === 0) return;
    setIndex((i) => (i + delta + count) % count);
  }, [count]);

  useEffect(() => {
    if (!multiple || arrowKeys !== 'global') return;
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack arrow keys from a text field (e.g. the resolve-note modal)
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiple, go, arrowKeys]);

  // `focused` mode: the keys are handled on the carousel itself and stopped there, so the
  // window-level record pager never sees the same press. Clicking anywhere inside counts as
  // "into the carousel" — the reader who clicked an arrow or a thumbnail is looking at
  // snapshots now, and shouldn't have to know that only Tab arms the keyboard.
  const focusRoot = () => { if (arrowKeys === 'focused') rootRef.current?.focus(); };
  const onRootKeyDown = (e: React.KeyboardEvent) => {
    if (arrowKeys !== 'focused' || !multiple) return;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    go(e.key === 'ArrowLeft' ? -1 : 1);
  };

  if (count === 0) return null;

  const onTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const current = images[index] || ({} as CarouselImage);

  return (
    <div
      ref={rootRef}
      className={`${className} ${arrowKeys === 'focused' ? 'rounded-lg outline-none focus:ring-2 focus:ring-brand/50' : ''}`}
      {...(arrowKeys === 'focused'
        ? { tabIndex: 0, [ARROW_SCOPE_ATTR]: 'images', onMouseDown: focusRoot, onKeyDown: onRootKeyDown }
        : {})}
    >
      <div
        className="relative overflow-hidden rounded-lg border border-border bg-black"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Slide track — all images stay mounted so switching doesn't refetch */}
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {images.map((img, i) => (
            <div key={i} className={`flex ${frameClassName || 'aspect-video'} w-full flex-shrink-0 items-center justify-center`}>
              {failed[i] ? (
                <span className="text-[12px] text-white/50">Image unavailable</span>
              ) : (
                <img
                  src={img.url}
                  alt={img.label || `Snapshot ${i + 1}`}
                  onClick={onImageClick ? () => onImageClick(img, i) : undefined}
                  className={`max-h-full max-w-full object-contain ${onImageClick ? 'cursor-zoom-in' : ''}`}
                  onError={() => setFailed((f) => ({ ...f, [i]: true }))}
                />
              )}
            </div>
          ))}
        </div>

        {/* What this frame IS, and when it was taken — one row of chips, because they are read
            together ("the face crop, at 2:22:07") and separated they read as two labels. */}
        {(current.label || badge) && (
          <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap items-center gap-1.5">
            {current.label && (
              <span className="rounded-md bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
                {current.label}
              </span>
            )}
            {badge && (
              <span className="rounded-md bg-black/75 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/90 backdrop-blur-sm">
                {badge}
              </span>
            )}
          </div>
        )}
        {/* No "3 / 4" counter: the thumbnail strip below names every frame AND shows which one
            is open, so a bare ordinal only added a number to read. */}
        {overlay && (
          <div className="pointer-events-none absolute bottom-3 right-3 rounded-lg bg-black/75 px-3 py-1.5 backdrop-blur-sm">
            {overlay}
          </div>
        )}

        {multiple && (
          <>
            <button
              onClick={() => go(-1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 transition-colors"
              title="Previous image"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 transition-colors"
              title="Next image"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails share the frame's width rather than sitting in a scrolling rail of 80px
          stamps: an event carries three or four frames, not thirty, and at that count the
          picker can be big enough to tell a face crop from the scene it came out of. Each
          carries its own caption — the strip is how the reader knows a "full scene" exists
          at all, which a row of unlabelled stamps never said. */}
      {multiple && (
        <div className="mt-3 grid gap-2.5" style={{ gridTemplateColumns: `repeat(${Math.min(count, 4)}, minmax(0, 1fr))` }}>
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`overflow-hidden rounded-lg border bg-card text-left transition-all ${
                i === index
                  ? 'border-brand ring-1 ring-brand/60'
                  : 'border-border hover:border-muted-foreground/60'
              }`}
              title={img.label || `Snapshot ${i + 1}`}
              aria-current={i === index}
            >
              <div className="flex h-16 items-center justify-center bg-black sm:h-20">
                {!failed[i] && <img src={img.url} alt="" className="h-full w-full object-cover" />}
              </div>
              <div
                className={`truncate border-t px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider ${
                  i === index ? 'border-brand/40 text-brand' : 'border-border text-muted-foreground'
                }`}
              >
                {img.label || `Frame ${i + 1}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
